import { z } from "zod";

/* Realtime protocol (docs/REALTIME.md). Client → server payloads are Zod schemas, validated on receipt.
   Server → client messages are types: the server builds them, clients trust them. */

export const SESSION_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"; // no O/0/I/1/L
export const SessionCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(new RegExp(`^[${SESSION_CODE_ALPHABET}]{4}$`), "codes are 4 letters or digits");

export const FREE_TEXT_MAX = 80;

export const PresenterAuth = z.object({
  role: z.literal("presenter"),
  code: SessionCode,
  presenterToken: z.string().min(1).max(200),
});
export const AudienceAuth = z.object({ role: z.literal("audience"), code: SessionCode });
export const LiveAuth = z.discriminatedUnion("role", [PresenterAuth, AudienceAuth]);
export type LiveAuth = z.infer<typeof LiveAuth>;

export const GotoPayload = z.object({ slideIndex: z.number().int().min(0), stop: z.number().int().min(0) });
export const LockPayload = z.object({ blockId: z.string().min(1).max(64), locked: z.boolean() });
export const RevealPayload = z.object({ blockId: z.string().min(1).max(64), revealed: z.boolean() });
export const VoteChoice = z.union([z.string().max(200), z.array(z.string().max(64)).max(6), z.number().int()]);
export type VoteChoice = z.infer<typeof VoteChoice>;
export const VotePayload = z.object({ blockId: z.string().min(1).max(64), choice: VoteChoice });
export const NicknamePayload = z.object({ nickname: z.string().max(100) });
export const UpvotePayload = z.object({
  blockId: z.string().min(1).max(64),
  key: z.string().min(1).max(200),
  on: z.boolean(),
});

export type PollKind = "single" | "multi" | "scale" | "wordcloud" | "open";

export type ActiveBlock = {
  blockId: string;
  type: "poll" | "quiz";
  question: string;
  kind: PollKind;
  options: { id: string; label: string }[];
  scale?: { min: number; max: number; minLabel?: string; maxLabel?: string };
  locked: boolean;
  revealed: boolean;
};

export type LiveReference = { id: string; title: string; authors?: string; venue?: string; year?: number; url: string };

export type SessionState = {
  code: string;
  status: "LIVE" | "ENDED";
  deckTitle: string;
  presenter: string;
  position: { slideIndex: number; stop: number };
  nowTitle: string;
  activeBlock: ActiveBlock | null;
  showReferences: boolean;
  /** Heading for the links: the references block's own title, or "Read more". */
  referencesTitle: string;
  references: LiveReference[];
};

/** A free-text answer: `key` is its lower-cased form (what counting and upvotes use), `up` its upvotes (open polls). */
export type Word = { key: string; text: string; n: number; up?: number };
export type Tally = {
  blockId: string;
  total: number;
  counts: Record<string, number>;
  words?: Word[];
};
export type PollResults = Tally & { correctOptionId?: string; explanation?: string };
export type VoteAck = { blockId: string; choice: VoteChoice };

/** Open-poll answers as phones see them while voting, so they can upvote each other. */
export type AnswersList = { blockId: string; answers: Word[] };
/** The answers this phone has upvoted, by key. */
export type UpvoteAck = { blockId: string; keys: string[] };

/** Quiz leaderboard. Only quizzes that have been revealed count, so it never hints at an answer. */
export type Leaderboard = { quizzes: number; entries: { name: string; score: number }[] };
/** What a phone knows about itself. */
export type ParticipantSelf = {
  nickname: string | null;
  score: number;
  quizzes: number;
  rank: number | null;
  of: number;
};
export type LiveError = { event: string; message: string };

/** Remove ASCII and C1 control characters from user text. */
export function stripControl(text: string): string {
  // eslint-disable-next-line no-control-regex -- matching control characters is the point
  return text.replace(/[\u0000-\u001f\u007f-\u009f]/g, "");
}

/** Strip control characters, collapse spaces, cap the length: the form free-text answers are stored in. */
export function normaliseText(text: string): string {
  return stripControl(text).replace(/\s+/g, " ").trim().slice(0, FREE_TEXT_MAX);
}

/** Socket.IO event map, server → client. Shared so the web client gets the same types. */
export interface ServerToClientEvents {
  "session:state": (s: SessionState) => void;
  "poll:tally": (t: Tally) => void;
  "poll:results": (r: PollResults) => void;
  "vote:ack": (a: VoteAck) => void;
  "live:error": (e: LiveError) => void;
  "answers:list": (a: AnswersList) => void;
  "upvote:ack": (a: UpvoteAck) => void;
  "leaderboard:state": (l: Leaderboard) => void;
  "participant:self": (p: ParticipantSelf) => void;
}

/** Client → server. Payloads are typed here for callers and still Zod-validated on the server. */
export interface ClientToServerEvents {
  "presenter:goto": (p: z.input<typeof GotoPayload>) => void;
  "poll:lock": (p: z.input<typeof LockPayload>) => void;
  "poll:reveal": (p: z.input<typeof RevealPayload>) => void;
  "session:end": (p: Record<string, never>) => void;
  "vote:cast": (p: z.input<typeof VotePayload>) => void;
  "participant:nickname": (p: z.input<typeof NicknamePayload>) => void;
  "answer:upvote": (p: z.input<typeof UpvotePayload>) => void;
}
